import STBBreadcrumbs from '@storeblocks/breadcrumbs';
import React from 'react';
import { NavLink, useParams } from 'react-router';
import styled from 'styled-components';

import { useBrandContext } from 'App/themes/themes.hooks';
import { Brand } from 'App/themes/themes.types';
import { RouteName } from 'Features/router';
import { linkPath } from 'Utils/navigation.utils';
import { getMaxScreenWidths } from 'Utils/screenSizes.utils';
import { getAppText } from 'Utils/text';

type CurrentPageTypes =
  | 'overview'
  | 'detailed'
  | 'change'
  | 'cancel'
  | 'invoices'
  | 'documentArchive'
  | 'contactUs'
  | 'secureMessage'
  | 'reregister';

export type BreadcrumbsProps = {
  currentPage: CurrentPageTypes;
};

export function Breadcrumbs({
  currentPage,
}: BreadcrumbsProps): React.ReactElement | null {
  const { brand } = useBrandContext();
  const { policyNumber } = useParams<{ policyNumber: string }>();

  const OverviewHomeLink = (
    <a key="overviewHome" href={linkPath('/oversikt')}>
      {getAppText('breadcrumb.overviewHome')}
    </a>
  );

  const OverviewInsuranceLink = (
    <NavLink key="overviewInsurance" to="/" reloadDocument>
      {getAppText('breadcrumb.overviewInsurance')}
    </NavLink>
  );

  const OverviewDetailsLink = (
    <NavLink
      key="overviewDetails"
      to={`${RouteName.INSURANCE_DETAILS}/${policyNumber}`}
    >
      {getAppText('breadcrumb.detailsPage')}
    </NavLink>
  );

  const ContactUsLink = (
    <NavLink to={RouteName.CONTACT_US}>
      {getAppText('breadcrumb.contactUsPage')}
    </NavLink>
  );

  function getLinks(): Array<React.ReactElement> {
    const isStbBrand = brand === Brand.STB;

    if (!isStbBrand) {
      switch (currentPage) {
        case 'overview':
          return [];
        case 'detailed':
        case 'invoices':
        case 'documentArchive':
        case 'contactUs':
          return [OverviewInsuranceLink];
        case 'change':
        case 'cancel':
        case 'reregister':
          return [OverviewInsuranceLink, OverviewDetailsLink];
        case 'secureMessage':
          return [OverviewInsuranceLink, ContactUsLink];
        default:
          return [];
      }
    }

    switch (currentPage) {
      case 'overview':
        return [OverviewHomeLink];
      case 'detailed':
      case 'invoices':
      case 'documentArchive':
      case 'contactUs':
        return [OverviewHomeLink, OverviewInsuranceLink];
      case 'change':
      case 'cancel':
      case 'reregister':
        return [OverviewHomeLink, OverviewInsuranceLink, OverviewDetailsLink];
      default:
        return [OverviewHomeLink];
    }
  }

  const links = getLinks();

  if (links.length === 0) {
    return null;
  }

  return (
    <Container>
      <STBBreadcrumbs>{links}</STBBreadcrumbs>
    </Container>
  );
}

const Container = styled.div`
  width: 100%;
  margin: 16px 0 16px;
  ${getMaxScreenWidths.tablet} {
    margin: 8px 0 16px;
  }
`;
